/**
 * Вкладка «История культур» — матрица «поле × 7 лет» с правкой клетки (AK-772).
 *
 * AK-899: матрица переехала со своего адреса `/farm/history?farm=` во вкладку
 * раздела «Данные хозяйства» (`/farm/<id>?tab=history`); старый адрес —
 * редирект. Хозяйство приходит из адреса раздела, а не из панели: раздел
 * адресуется хозяйством, и вкладка не может показывать другое.
 *
 * Wireframe v3, одобрен founder 31.08 (`10-farm-card-onboarding.html`, блок
 * «История посевов»). Экран отвечает на один вопрос — «где в истории дырки и
 * как их закрыть», — и поэтому устроен иначе, чем «Аналитика прошлых лет»:
 *
 * * там строки — поля, У КОТОРЫХ история есть (`EXISTS` в SQL аналитики),
 *   здесь главный клиент — поле, у которого её НЕТ вовсе;
 * * там клик по строке открывает карточку поля, здесь клик по клетке открывает
 *   правку года;
 * * там единица измерения — поля с урожайностью, здесь — клетки пашни, ровно
 *   те же, что считает шаг подготовки.
 *
 * Что экран НЕ заводит своего: форму года (внутри диалога стоит штатный
 * `RotationYearEditor`), писателя истории (`PUT /fields/{id}/rotation/{year}`)
 * и контрол выбора хозяйства.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Lock, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { farmPrepKeys } from '../preparation';
import { cropHistoryKeys, useCropHistoryGrid, PAGE_SIZE, type HistoryCell } from './api';
import { progressPct, progressText, rowCells, type CellView } from './cellView';
import { CellDialog, type CellTarget } from './CellDialog';

function CellButton({
  view,
  onOpen,
}: {
  view: CellView;
  onOpen: () => void;
}) {
  const body = (
    <button
      type="button"
      disabled={!view.editable}
      onClick={onOpen}
      data-testid={`cell-${view.year}`}
      className={cn(
        'w-full rounded-md px-1 py-1 text-center text-xs transition-colors',
        view.state === 'empty' && 'border border-dashed border-border text-muted-foreground',
        view.state === 'fallow' && 'bg-muted font-medium text-foreground',
        view.state === 'crop' && 'bg-primary/10 font-medium text-foreground',
        view.editable ? 'hover:bg-accent' : 'cursor-not-allowed opacity-70',
      )}
    >
      <span className="flex items-center justify-center gap-1">
        {view.label}
        {!view.editable && view.state !== 'empty' && <Lock className="h-3 w-3 shrink-0" />}
      </span>
      {view.yieldText && <span className="block text-muted-foreground">{view.yieldText}</span>}
      {view.manual && (
        <span className="block text-[10px] text-accent-brand">изменено вручную</span>
      )}
    </button>
  );
  if (!view.lockReason) return body;
  return (
    <Tooltip>
      {/* Кнопка disabled событий мыши не даёт — оборачиваем в span, иначе на
          замочке не было бы подсказки, и человек толкал бы клетку молча. */}
      <TooltipTrigger asChild>
        <span tabIndex={0}>{body}</span>
      </TooltipTrigger>
      <TooltipContent>{view.lockReason}</TooltipContent>
    </Tooltip>
  );
}

export function FarmCropHistoryMatrix({ farmId }: { farmId: string }) {
  const qc = useQueryClient();
  const [onlyGaps, setOnlyGaps] = useState(false);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [target, setTarget] = useState<CellTarget | null>(null);

  const grid = useCropHistoryGrid(farmId, { onlyGaps, search, offset });
  const data = grid.data;
  const currentYear = data ? data.years[data.years.length - 1] : new Date().getFullYear();

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: cropHistoryKeys.farm(farmId) });
    // Шаг «История культур» полосы готовности считается ТОЙ ЖЕ ручкой
    // подготовки — без этой строки человек чинит клетки, а полоса стоит.
    void qc.invalidateQueries({ queryKey: farmPrepKeys.farm(farmId) });
  };

  const openCell = (
    fieldId: string,
    fieldName: string,
    year: number,
    cell: HistoryCell | undefined,
  ) => {
    setTarget({ farmId, fieldId, fieldName, year, cell });
  };

  return (
    <Stack gap={4} data-testid="crop-history-matrix">
      <p className="text-sm text-muted-foreground">
        Что росло на поле в прошлые годы. Без этого на поле не работает правило севооборота:
        система не знает предшественника.
      </p>

      {grid.isLoading && <Skeleton className="h-64 w-full" />}

      {grid.isError && (
        <Card>
          <CardContent className="p-4 text-sm text-destructive">
            Не получилось прочитать историю хозяйства. Обновите страницу — если не поможет,
            напишите администратору.
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          <Card>
            <CardContent className="space-y-3 p-4">
              <div className="text-sm font-medium text-foreground" data-testid="history-progress">
                {progressText(data)}
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full bg-primary" style={{ width: `${progressPct(data)}%` }} />
              </div>
              <p className="text-sm text-muted-foreground">
                «Пар» — это заполненный год, а не пустой: поле с честным паром считается
                разобранным.
                {!data.can_overwrite &&
                  ' Заполненные годы у вас под замком — переписать их может руководитель хозяйства.'}
              </p>
            </CardContent>
          </Card>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant={onlyGaps ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setOnlyGaps((v) => !v);
                setOffset(0);
              }}
              data-testid="filter-gaps"
            >
              Есть пустые годы
            </Button>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="w-56 pl-8"
                placeholder="Номер или имя поля"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setOffset(0);
                }}
              />
            </div>
            <span className="ml-auto text-sm text-muted-foreground">
              Полей в выборке: {data.total}
            </span>
          </div>

          <Card>
            <CardContent className="overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[18%]">Поле</TableHead>
                    <TableHead className="text-right">Га</TableHead>
                    {data.years.map((y) => (
                      <TableHead key={y} className="text-center">
                        {y}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((row) => {
                    const byYear = new Map(row.cells.map((c) => [c.year, c]));
                    return (
                      <TableRow key={row.field_id}>
                        <TableCell>
                          <div className="font-medium">{row.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {row.brigade_name ?? 'без отделения'}
                            {row.empty_years > 0 && (
                              <Badge variant="outline" className="ml-2">
                                пусто {row.empty_years}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.area_ha === null ? '—' : row.area_ha.toLocaleString('ru-RU')}
                        </TableCell>
                        {rowCells(row, data.years, data.can_overwrite, currentYear).map((v) => (
                          <TableCell key={v.year} className="align-top">
                            <CellButton
                              view={v}
                              onOpen={() =>
                                openCell(row.field_id, row.name, v.year, byYear.get(v.year))
                              }
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                  {data.items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={data.years.length + 2} className="p-6 text-center text-sm text-muted-foreground">
                        Полей по этому фильтру нет.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {data.total > PAGE_SIZE && (
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Назад
              </Button>
              <span className="text-sm text-muted-foreground">
                {offset + 1}–{Math.min(offset + PAGE_SIZE, data.total)} из {data.total}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={offset + PAGE_SIZE >= data.total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Дальше
              </Button>
            </div>
          )}
        </>
      )}

      <CellDialog target={target} onClose={() => setTarget(null)} onSaved={invalidate} />
    </Stack>
  );
}
